import React from 'react'
import { FieldErrors, SubmitHandler, SubmitErrorHandler } from 'react-hook-form'
import get from 'lodash/get'
import ControlledTextField from 'transfix-form/components/ControlledTextInput'
import { Button } from 'transfix-ui/components/Button'
import { INLINE_MESSAGE_VARIANTS, InlineMessage } from 'transfix-ui/components/InlineMessage'
import { Box } from 'transfix-ui/core'
import StepButtonContainer from '../../../StepButtonContainer'
import { TVerifyPasswordSchema } from './VerifyPasswordSchema'
import { useVerifyPasswordForm } from './useVerifyPasswordForm'

const getErrorMessage = <T extends FieldErrors<TVerifyPasswordSchema>, K extends keyof T>(
  errors: T,
  keyName: K
): string | undefined => {
  return get(errors, [keyName, 'message'])
}

export interface IVerifyPasswordFormProps {
  /**
   * @default {}
   */
  defaultValues?: Partial<TVerifyPasswordSchema>
  /**
   * @example (data) => {}
   */
  onSubmitSuccess: SubmitHandler<TVerifyPasswordSchema>
  /**
   * @example (errors) => {}
   */
  onSubmitError?: SubmitErrorHandler<TVerifyPasswordSchema>
  onCancel?: () => void
  /**
   * When provided, displays an inline alert this message
   * @example = 'Sorry, we didn't recognize that password.'
   */
  alertMessage?: string
  loading?: boolean
}

export const VerifyPasswordForm = ({
  defaultValues = {},
  onSubmitSuccess,
  onSubmitError = () => {},
  onCancel,
  alertMessage,
  loading,
}: IVerifyPasswordFormProps) => {
  const { control, errors, handleSubmit } = useVerifyPasswordForm({
    defaultValues,
  })

  const submitHandler = handleSubmit(onSubmitSuccess, onSubmitError)

  return (
    <form onSubmit={submitHandler} data-test='VerifyPasswordForm'>
      <ControlledTextField
        name='password'
        control={control}
        TextInputProps={{
          type: 'password',
          label: 'Password',
          validationMessage: getErrorMessage(errors, 'password'),
          error: !!getErrorMessage(errors, 'password'),
          placeholder: 'Enter Your Password',
          dataTest: 'input-password',
        }}
      />
      {alertMessage && (
        <Box paddingTop='12px'>
          <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={alertMessage} />
        </Box>
      )}
      <StepButtonContainer>
        <Button onClick={onCancel} color='tertiary'>
          Cancel
        </Button>
        <Button
          type='submit'
          color='hotPrimary'
          size='large'
          data-test='submit-button'
          disabled={loading}>
          Permanently Delete Account
        </Button>
      </StepButtonContainer>
    </form>
  )
}
